import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { Eye, FileText, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/currency";

/**
 * Super-admin (and platform owner) live view of every bid on an auction, with
 * full identities and every field the bidder filled in. Every access is
 * audit-logged server-side.
 *
 * Bid rows adapt to the auction type:
 *   - STANDARD: single scalar price + delivery/payment/warranty
 *   - BOP: total price + expandable per-line unit prices
 *   - FREIGHT: total (sum of quoted rates) + expandable per-shipment
 *              vessel / committed ETD / rate — this is what the creator needs
 *              to see mid-auction to verify vessel commitments.
 * Every row now also shows a full Remarks cell + downloadable attachment.
 */
export const SuperAdminPeek = ({ auctionId }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // {rowKey: true}

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/auctions/${auctionId}/raw-bids`);
      setData(res.data);
      setOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (k) => setExpanded((s) => ({ ...s, [k]: !s[k] }));

  const isFreight = data?.type === "freight";
  const isBop = data?.type === "bop";
  const isStandard = !isFreight && !isBop;

  return (
    <>
      <Button
        variant="outline"
        onClick={load}
        disabled={loading}
        className="rounded-none border-[#22D3EE] text-[#22D3EE] hover:bg-[#F1F5FF] h-9 px-3 text-xs tracking-[0.18em] uppercase font-semibold"
        data-testid="super-admin-peek-btn"
      >
        <Eye size={14} weight="bold" className="mr-2" />
        {loading ? "Loading..." : "Peek (super-admin)"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-6xl" data-testid="super-admin-peek-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">
              <ShieldCheck size={12} weight="bold" /> Super-admin · audit logged
            </div>
            <DialogTitle className="font-display text-2xl">All Bids — Live View</DialogTitle>
          </DialogHeader>
          {data && (
            <>
              <div className="text-xs text-neutral-600 mb-3">
                <span className="font-semibold">{data.title}</span> · status <span className="font-mono-plex">{data.status}</span> · round R{data.current_round} · {data.bid_count} bid{data.bid_count === 1 ? "" : "s"} · type <span className="uppercase tracking-widest text-[10px] font-semibold text-[#0D9488]">{data.type || "standard"}</span>
              </div>
              {data.bids.length === 0 ? (
                <div className="text-sm text-neutral-500 border border-dashed border-black/15 p-6 text-center">
                  No bids submitted yet.
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[65vh]">
                  <table className="w-full border-collapse text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-black/30 text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                        <th className="text-left py-2 pr-3 w-10"></th>
                        <th className="text-left py-2 pr-3 w-8">R</th>
                        <th className="text-left py-2 pr-3">Bidder</th>
                        <th className="text-left py-2 pr-3">Company</th>
                        <th className="text-right py-2 pr-3">{isFreight ? "Total" : "Price"}</th>
                        {isStandard && <th className="text-left py-2 pr-3">Delivery</th>}
                        {isStandard && <th className="text-left py-2 pr-3">Payment</th>}
                        {isStandard && <th className="text-left py-2 pr-3">Warranty</th>}
                        <th className="text-left py-2 pr-3">Remarks</th>
                        <th className="text-left py-2 pr-3">Submitted</th>
                        <th className="text-left py-2 pr-3">File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.bids.map((b, idx) => {
                        const rowKey = `${b.bidder_id}-${b.round}-${idx}`;
                        const isOpen = !!expanded[rowKey];
                        const canExpand =
                          (isFreight && Array.isArray(b.shipment_bids) && b.shipment_bids.length > 0) ||
                          (isBop && b.line_prices && Object.keys(b.line_prices).length > 0);
                        return (
                          <React.Fragment key={rowKey}>
                            <tr className="border-b border-black/10 hover:bg-neutral-50" data-testid={`peek-bid-row-${idx}`}>
                              <td className="py-2 pr-3">
                                {canExpand ? (
                                  <button
                                    type="button"
                                    onClick={() => toggle(rowKey)}
                                    className="w-6 h-6 border border-black/20 flex items-center justify-center text-xs font-bold hover:bg-[#22D3EE] hover:border-[#22D3EE] hover:text-[#0B1929]"
                                    data-testid={`peek-expand-${idx}`}
                                    title="Show per-shipment / per-line details"
                                  >
                                    {isOpen ? "−" : "+"}
                                  </button>
                                ) : null}
                              </td>
                              <td className="py-2 pr-3 font-mono-plex">R{b.round}</td>
                              <td className="py-2 pr-3">
                                <div className="font-semibold">{b.bidder_name}</div>
                                <div className="text-[11px] font-mono-plex text-neutral-500">{b.bidder_email}</div>
                              </td>
                              <td className="py-2 pr-3 text-neutral-600">{b.bidder_company || "—"}</td>
                              <td className="py-2 pr-3 text-right font-mono-plex font-semibold text-[#0D9488]">
                                {formatCurrency(b.price, data.currency)}
                                {isFreight && Array.isArray(b.shipment_bids) && (
                                  <div className="text-[10px] font-normal tracking-widest uppercase text-neutral-400 mt-0.5">
                                    {b.shipment_bids.length} shipment{b.shipment_bids.length === 1 ? "" : "s"} quoted
                                  </div>
                                )}
                              </td>
                              {isStandard && <td className="py-2 pr-3">{b.delivery_timeline || "—"}</td>}
                              {isStandard && <td className="py-2 pr-3">{b.payment_terms || "—"}</td>}
                              {isStandard && <td className="py-2 pr-3">{b.warranty || "—"}</td>}
                              <td className="py-2 pr-3 max-w-[240px]" data-testid={`peek-remarks-${idx}`}>
                                {b.remarks
                                  ? <div className="text-[12px] leading-snug whitespace-pre-wrap break-words text-neutral-800">{b.remarks}</div>
                                  : <span className="text-neutral-400">—</span>}
                              </td>
                              <td className="py-2 pr-3 text-[11px] font-mono-plex text-neutral-500 whitespace-nowrap">{b.submitted_at ? new Date(b.submitted_at).toLocaleString() : "—"}</td>
                              <td className="py-2 pr-3">
                                {b.attachment_path ? (
                                  <a
                                    href={`${API}/files/download?path=${encodeURIComponent(b.attachment_path)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[11px] uppercase tracking-[0.18em] font-semibold underline underline-offset-4"
                                  >
                                    <FileText size={12} weight="bold" className="inline mr-1" />view
                                  </a>
                                ) : <span className="text-neutral-400">—</span>}
                              </td>
                            </tr>
                            {isOpen && isFreight && Array.isArray(b.shipment_bids) && (
                              <tr className="bg-[#F1FBFB]" data-testid={`peek-shipments-${idx}`}>
                                <td></td>
                                <td colSpan={9} className="py-3 pr-3">
                                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-2 font-semibold">Per-Shipment Quotes</div>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-[#0D9488]/30 text-[10px] tracking-[0.2em] uppercase text-neutral-500">
                                        <th className="text-left py-1 pr-3">Shipment</th>
                                        <th className="text-left py-1 pr-3">Container Pickup</th>
                                        <th className="text-left py-1 pr-3">Vessel</th>
                                        <th className="text-left py-1 pr-3">Committed ETD</th>
                                        <th className="text-right py-1 pr-3">Rate</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {b.shipment_bids.map((sb, si) => {
                                        const ship = (data.shipments || []).find((s) => s.shipment_ref === sb.shipment_ref);
                                        return (
                                          <tr key={si} className="border-b border-black/5">
                                            <td className="py-1 pr-3 font-mono-plex font-semibold">{sb.shipment_ref}</td>
                                            <td className="py-1 pr-3 font-mono-plex">{ship?.cargo_ready_date || "—"}</td>
                                            <td className="py-1 pr-3">{sb.vessel_name || "—"}</td>
                                            <td className="py-1 pr-3 font-mono-plex">{sb.etd_committed || "—"}</td>
                                            <td className="py-1 pr-3 text-right font-mono-plex text-[#0D9488]">{formatCurrency(sb.rate, data.currency)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                            {isOpen && isBop && b.line_prices && (
                              <tr className="bg-[#F1FBFB]" data-testid={`peek-lines-${idx}`}>
                                <td></td>
                                <td colSpan={9} className="py-3 pr-3">
                                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-2 font-semibold">Per-Line Quotes</div>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-[#0D9488]/30 text-[10px] tracking-[0.2em] uppercase text-neutral-500">
                                        <th className="text-left py-1 pr-3">Item</th>
                                        <th className="text-left py-1 pr-3">Description</th>
                                        <th className="text-right py-1 pr-3">Qty</th>
                                        <th className="text-right py-1 pr-3">Unit Price</th>
                                        <th className="text-right py-1 pr-3">Line Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(data.line_items || []).map((li) => {
                                        const up = b.line_prices[li.item_number];
                                        const lt = up != null ? up * (li.forecast_qty || 0) : null;
                                        return (
                                          <tr key={li.item_number} className="border-b border-black/5">
                                            <td className="py-1 pr-3 font-mono-plex font-semibold">{li.item_number}</td>
                                            <td className="py-1 pr-3">{li.item_desc}</td>
                                            <td className="py-1 pr-3 text-right font-mono-plex">{Number(li.forecast_qty || 0).toLocaleString()}</td>
                                            <td className="py-1 pr-3 text-right font-mono-plex">{up != null ? formatCurrency(up, data.currency) : "—"}</td>
                                            <td className="py-1 pr-3 text-right font-mono-plex text-[#0D9488]">{lt != null ? formatCurrency(lt, data.currency) : "—"}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="text-[11px] text-neutral-500 mt-3 leading-snug flex items-center justify-between gap-3 flex-wrap">
                <span>
                  Identities are shown only to you because you are the platform super-admin. Every access is recorded. Click the <strong>+</strong> next to a bid to see per-shipment or per-line details.
                </span>
                <Link
                  to={`/super-admin/peek-log?auction_id=${auctionId}`}
                  className="text-[11px] uppercase tracking-[0.2em] font-semibold underline underline-offset-4 text-[#22D3EE] whitespace-nowrap"
                  data-testid="view-peek-log-link"
                >
                  View peek log →
                </Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
